import Raf from "quark-raf";
import { module } from 'modujs';
import {Renderer, Camera, Transform, Vec3, Vec2, Program, GLTFLoader, Post } from 'ogl';
import vertexShader from "../shaders/globeVertex";
import fragmentShader from "../shaders/globeFragment";
import Tween from "../class/Tween";
import { queryClosestParent } from '../utils/html';

// WIP - Try Post processing
const fragment = /* glsl */ `
    precision highp float;

    // Default uniform for previous pass is 'tMap'.
    // Can change this using the 'textureUniform' property
    // when adding a pass.
    uniform sampler2D tMap;
    uniform vec2 uResolution;
    varying vec2 vUv;

    vec3 mod289(vec3 x) {
        return x - floor(x * (1.0 / 289.0)) * 289.0;
    }

    vec4 mod289(vec4 x) {
        return x - floor(x * (1.0 / 289.0)) * 289.0;
    }

    vec4 permute(vec4 x) {
        return mod289(((x*34.0)+1.0)*x);
    }

    vec4 taylorInvSqrt(vec4 r) {
        return 1.79284291400159 - 0.85373472095314 * r;
    }

    vec3 fade(vec3 t) {
        return t*t*t*(t*(t*6.0-15.0)+10.0);
    }

    float cnoise(vec3 P) {
        vec3 Pi0 = floor(P); // Integer part for indexing
        vec3 Pi1 = Pi0 + vec3(1.0); // Integer part + 1
        Pi0 = mod289(Pi0);
        Pi1 = mod289(Pi1);
        vec3 Pf0 = fract(P); // Fractional part for interpolation
        vec3 Pf1 = Pf0 - vec3(1.0); // Fractional part - 1.0
        vec4 ix = vec4(Pi0.x, Pi1.x, Pi0.x, Pi1.x);
        vec4 iy = vec4(Pi0.yy, Pi1.yy);
        vec4 iz0 = Pi0.zzzz;
        vec4 iz1 = Pi1.zzzz;

        vec4 ixy = permute(permute(ix) + iy);
        vec4 ixy0 = permute(ixy + iz0);
        vec4 ixy1 = permute(ixy + iz1);

        vec4 gx0 = ixy0 * (1.0 / 7.0);
        vec4 gy0 = fract(floor(gx0) * (1.0 / 7.0)) - 0.5;
        gx0 = fract(gx0);
        vec4 gz0 = vec4(0.5) - abs(gx0) - abs(gy0);
        vec4 sz0 = step(gz0, vec4(0.0));
        gx0 -= sz0 * (step(0.0, gx0) - 0.5);
        gy0 -= sz0 * (step(0.0, gy0) - 0.5);

        vec4 gx1 = ixy1 * (1.0 / 7.0);
        vec4 gy1 = fract(floor(gx1) * (1.0 / 7.0)) - 0.5;
        gx1 = fract(gx1);
        vec4 gz1 = vec4(0.5) - abs(gx1) - abs(gy1);
        vec4 sz1 = step(gz1, vec4(0.0));
        gx1 -= sz1 * (step(0.0, gx1) - 0.5);
        gy1 -= sz1 * (step(0.0, gy1) - 0.5);

        vec3 g000 = vec3(gx0.x,gy0.x,gz0.x);
        vec3 g100 = vec3(gx0.y,gy0.y,gz0.y);
        vec3 g010 = vec3(gx0.z,gy0.z,gz0.z);
        vec3 g110 = vec3(gx0.w,gy0.w,gz0.w);
        vec3 g001 = vec3(gx1.x,gy1.x,gz1.x);
        vec3 g101 = vec3(gx1.y,gy1.y,gz1.y);
        vec3 g011 = vec3(gx1.z,gy1.z,gz1.z);
        vec3 g111 = vec3(gx1.w,gy1.w,gz1.w);

        vec4 norm0 = taylorInvSqrt(vec4(dot(g000, g000), dot(g010, g010), dot(g100, g100), dot(g110, g110)));
        g000 *= norm0.x;
        g010 *= norm0.y;
        g100 *= norm0.z;
        g110 *= norm0.w;
        vec4 norm1 = taylorInvSqrt(vec4(dot(g001, g001), dot(g011, g011), dot(g101, g101), dot(g111, g111)));
        g001 *= norm1.x;
        g011 *= norm1.y;
        g101 *= norm1.z;
        g111 *= norm1.w;

        float n000 = dot(g000, Pf0);
        float n100 = dot(g100, vec3(Pf1.x, Pf0.yz));
        float n010 = dot(g010, vec3(Pf0.x, Pf1.y, Pf0.z));
        float n110 = dot(g110, vec3(Pf1.xy, Pf0.z));
        float n001 = dot(g001, vec3(Pf0.xy, Pf1.z));
        float n101 = dot(g101, vec3(Pf1.x, Pf0.y, Pf1.z));
        float n011 = dot(g011, vec3(Pf0.x, Pf1.yz));
        float n111 = dot(g111, Pf1);

        vec3 fade_xyz = fade(Pf0);
        vec4 n_z = mix(vec4(n000, n100, n010, n110), vec4(n001, n101, n011, n111), fade_xyz.z);
        vec2 n_yz = mix(n_z.xy, n_z.zw, fade_xyz.y);
        float n_xyz = mix(n_yz.x, n_yz.y, fade_xyz.x);
        return 2.2 * n_xyz;
    }

    vec4 fxaa(sampler2D tex, vec2 uv, vec2 resolution) {
        vec2 pixel = vec2(1) / resolution;
        vec3 l = vec3(0.299, 0.587, 0.114);
        float lNW = dot(texture2D(tex, uv + vec2(-1, -1) * pixel).rgb, l);
        float lNE = dot(texture2D(tex, uv + vec2( 1, -1) * pixel).rgb, l);
        float lSW = dot(texture2D(tex, uv + vec2(-1,  1) * pixel).rgb, l);
        float lSE = dot(texture2D(tex, uv + vec2( 1,  1) * pixel).rgb, l);
        float lM  = dot(texture2D(tex, uv).rgb, l);
        float lMin = min(lM, min(min(lNW, lNE), min(lSW, lSE)));
        float lMax = max(lM, max(max(lNW, lNE), max(lSW, lSE)));

        vec2 dir = vec2(
            -((lNW + lNE) - (lSW + lSE)),
            ((lNW + lSW) - (lNE + lSE))
        );

        float dirReduce = max((lNW + lNE + lSW + lSE) * 0.03125, 0.0078125);
        float rcpDirMin = 1.0 / (min(abs(dir.x), abs(dir.y)) + dirReduce);
        dir = min(vec2(8, 8), max(vec2(-8, -8), dir * rcpDirMin)) * pixel;

        vec3 rgbA = 0.5 * (
            texture2D(tex, uv + dir * (1.0 / 3.0 - 0.5)).rgb +
            texture2D(tex, uv + dir * (2.0 / 3.0 - 0.5)).rgb);
        vec3 rgbB = rgbA * 0.5 + 0.25 * (
            texture2D(tex, uv + dir * -0.5).rgb +
            texture2D(tex, uv + dir * 0.5).rgb);
        float lB = dot(rgbB, l);
        return mix(
            vec4(rgbB, 1),
            vec4(rgbA, 1),
            max(sign(lB - lMin), 0.0) * max(sign(lB - lMax), 0.0)
        );
    }


    void main() {
        vec2 uv = vUv;

        float noise = cnoise(vec3(uv, cos(.1 * 0.1)) * 2.0 + .1 * 0.5);

        uv.x += noise * .1;
        uv.y += noise * .1;


        vec4 raw = texture2D(tMap, uv);
        vec4 aa = fxaa(tMap, uv, uResolution);

        gl_FragColor = raw;
    }
`;

export default class extends module {
    constructor(m) {
        super(m);

        // Flag
        this.isPlaying = false
        this.isGltfLoaded = false

        // Metrics
        this.wrapperBCR = this.el.getBoundingClientRect();

        // Functions binding
        this.onRender = this.onRender.bind(this)
        this.onResize = this.onResize.bind(this);
        this.onEnter = this.onEnter.bind(this);
        this.onLeave = this.onLeave.bind(this);

        // GLTF Values
        this.gltfUrl = this.getData('gltf')
        this.gltfColors = [
            {
                value: this.getData('wires-color') ? this.hexToGlsl(this.getData('wires-color')) : new Vec3(0.,0.886,0.929)
            },
        ]

        this.gltfDefaultColorRGB = this.getData('wires-color') ? this.hexToRgb(this.getData('wires-color')) : {r: 0, g: 0, b:0}
        this.gltfHoverColorRGB = this.getData('wires-color-hover') ? this.hexToRgb(this.getData('wires-color-hover')) : {r: 0, g: 0, b:0}


        // Programs
        this.programs = []

        // Tweak Values
        this.rotationX = 0.01
        this.rotationY = 0.01
        this.rotationZ = 0.00
        this.light = 0
    }

    /////////////////////////
    // Lifecycle
    init() {
        // Call
        this.bindUI();
        this.bindEvents();
        this.initCanvas();

        this.hoverEnterTween = new Tween({
            from: 0,
            to: 1,
            duration: 0,
            onProgress: (progress) => {
                this.animeEnterColor(progress)
            }
        })
        this.hoverLeaveTween = new Tween({
            from: 0,
            to: 1,
            duration: 0,
            onProgress: (progress) => {
                this.animeLeaveColor(progress)
            }
        })
    }

    destroy() {
        super.destroy();
        this.unbindEvents();
        this.onStop();
    }


    /////////////////////////
    // Binding
    bindUI() {
        this.ui = {};
        this.ui.$el = this.el;
        this.ui.$wrapper = this.$("wrap")[0];
        this.ui.$panel = this.$("panel")[0];
        this.ui.$parent = queryClosestParent(this.ui.$el, '.c-phone')
    }

    bindEvents() {
        window.addEventListener("resize", this.onResize);
        this.ui.$parent.addEventListener('pointerenter', this.onEnter)
        this.ui.$parent.addEventListener('pointerleave', this.onLeave)
    }

    unbindEvents() {
        window.removeEventListener("resize", this.onResize);
        this.ui.$parent.removeEventListener('pointerenter', this.onEnter)
        this.ui.$parent.removeEventListener('pointerleave', this.onLeave)
    }


    /////////////////////////
    // Init Canvas
    async initCanvas() {
        // Init Renderer
        this.renderer = new Renderer({
            dpr: 1,
            antialias: true,
            webgl: 1,
            alpha: true,
        });
        this.renderer.setSize(this.wrapperBCR.width, this.wrapperBCR.height);

        // Store renderer
        this.gl = this.renderer.gl;
        // Then add to the DOM
        this.ui.$wrapper.appendChild(this.gl.canvas);

        // Post copies the current renderer values (width, height, dpr) if none are passed in
        this.post = new Post(this.gl);

        // Set resolution
        this.uResolution = {value: new Vec2(this.gl.canvas.width, this.gl.canvas.height)};

        // Call
        this.initScene();
        this.initCamera();
        await this.initShape();

        // update size
        this.onResize(true);

        // Play auto
        this.onRender()
    }

    initScene() {
        this.scene = new Transform();
    }

    initCamera() {
        this.camera = new Camera(this.gl)
        this.camera.fov = 45
        this.camera.position.z = 2.65;
        this.camera.lookAt([0, 0, 0]);
    }

    async initShape() {
        // Gltf
        const formattedGltfUrl = this.gltfUrl.split("?")[0]
        this.gltf = await GLTFLoader.load(this.gl, formattedGltfUrl);
        this.addGltf(this.gltf)

        const pass = this.post.addPass({
            // If not passed in, pass will use the default vertex/fragment
            // shaders found within the class.
            fragment,
            uniforms: {
                uResolution: this.uResolution,
            },
        });
    }

    onEnter() {
        this.hoverEnterTween?.start()
    }

    onLeave() {
        this.hoverLeaveTween?.start()
    }

    /////////////////////////
    // Callbacks
    onToggle() {
        if (this.isPlaying) {
            this.onStop()
        } else if (!this.isPlaying) {
            this.onPlay()
        }
    }

    onPlay() {
        if (this.isPlaying) return
        this.isPlaying = true
        Raf.add(this.onRender);
    }

    onStop() {
        if (!this.isPlaying) return
        this.isPlaying = false
        Raf.remove(this.onRender);
    }

    onResize(force = false) {
        // Check if I need to update size
        const newElWidth = this.el.offsetWidth
        const newElHeight = this.el.offsetHeight
        if(newElWidth == this.wrapperBCR.width && !force || newElHeight == this.wrapperBCR.height && !force) return

        // Metrics
        this.wrapperBCR = this.el.getBoundingClientRect();

        // Set canvas size
        this.gl.canvas.style.width = `${this.wrapperBCR.width}px`;
        this.gl.canvas.style.height = `${this.wrapperBCR.height}px`;

        // Set renderer size
        this.renderer.setSize(this.wrapperBCR.width, this.wrapperBCR.height);

        // Then Camera perspective
        this.aspect = this.wrapperBCR.width / this.wrapperBCR.height;
        this.camera.perspective({ aspect: this.aspect});

        this.post.resize()
        this.uResolution = {value: new Vec2(this.gl.canvas.width, this.gl.canvas.height)}
    }

    onRender() {
        if (!this.isGltfLoaded) return

        // Post processing
        //this.post.render({scene: this.scene, camera: this.camera, sort: false, frustumCull: false});

        // Or classic renderer
        this.renderer.render({scene: this.scene, camera: this.camera, sort: false, frustumCull: false});

        this.scene.children[0].rotation.x += this.rotationX;
        this.scene.children[0].rotation.y += this.rotationY;
        this.scene.children[0].rotation.z += this.rotationZ;
    }

    /////////////////////////
    // Methods
    addGltf() {
        const gltfScene = this.gltf.scene
        gltfScene.forEach((root, index) => {
            root.setParent(this.scene);
            root.traverse((node) => {
                if (node.program) {
                    node.program = this.createProgram(node, index);
                    this.programs.push(node.program)
                }
            });
        });

        // Calculate world matrices for bounds<†
        this.scene.updateMatrixWorld();

        // set gltf loaded
        this.isGltfLoaded = true
    }

    createProgram(node, index) {
        const gltf = node.program.gltfMaterial || {};

        const vertexPrefix = this.renderer.isWebgl2
            ? /* glsl */ `#version 300 es
            #define attribute in
            #define varying out
            #define texture2D texture
        `
            : ``;

        const fragmentPrefix = this.renderer.isWebgl2
            ? /* glsl */ `#version 300 es
            precision highp float;
            #define varying in
            #define texture2D texture
            #define gl_FragColor FragColor
            out vec4 FragColor;
        `
            : /* glsl */ `#extension GL_OES_standard_derivatives : enable
            precision highp float;
        `;

        let defines = `
            ${node.geometry.attributes.uv ? `#define UV` : ``}
            ${node.geometry.attributes.normal ? `#define NORMAL` : ``}
            ${node.geometry.isInstanced ? `#define INSTANCED` : ``}
            ${node.boneTexture ? `#define SKINNING` : ``}
            ${gltf.alphaMode === 'MASK' ? `#define ALPHA_MASK` : ``}
            ${gltf.baseColorTexture ? `#define COLOR_MAP` : ``}
            ${gltf.normalTexture ? `#define NORMAL_MAP` : ``}
            ${gltf.metallicRoughnessTexture ? `#define RM_MAP` : ``}
            ${gltf.occlusionTexture ? `#define OCC_MAP` : ``}
            ${gltf.emissiveTexture ? `#define EMISSIVE_MAP` : ``}
        `;

        let vertex = vertexPrefix + defines + vertexShader;
        let fragment = fragmentPrefix + defines + fragmentShader;

        const program = new Program(this.gl, {
            vertex,
            fragment,
            uniforms: {
                uColor: this.gltfColors[index],
                uLight: { value: this.light },
            },
            transparent: gltf.alphaMode === 'BLEND',
            cullFace: gltf.doubleSided ? null : this.gl.BACK,
        });

        return program;
    }

    animeEnterColor(progress) {
        const newR = this.gltfHoverColorRGB.r
        const newG = this.gltfHoverColorRGB.g
        const newB = this.gltfHoverColorRGB.b

        const oldR = this.gltfDefaultColorRGB.r
        const oldG = this.gltfDefaultColorRGB.g
        const oldB = this.gltfDefaultColorRGB.b

        const diffR = newR - oldR
        const diffG = newG - oldG
        const diffB = newB - oldB


        this.programs[0].uniforms.uColor.value = new Vec3((oldR + (diffR * progress)) / 255, (oldG + (diffG * progress)) / 255, (oldB + (diffB * progress)) / 255)
    }

    animeLeaveColor(progress) {
        const newR = this.gltfDefaultColorRGB.r
        const newG = this.gltfDefaultColorRGB.g
        const newB = this.gltfDefaultColorRGB.b

        const oldR = this.gltfHoverColorRGB.r
        const oldG = this.gltfHoverColorRGB.g
        const oldB = this.gltfHoverColorRGB.b

        const diffR = newR - oldR
        const diffG = newG - oldG
        const diffB = newB - oldB


        this.programs[0].uniforms.uColor.value = new Vec3((oldR + (diffR * progress)) / 255, (oldG + (diffG * progress)) / 255, (oldB + (diffB * progress)) / 255)
    }

    hexToRgb(hex) {
        const convertedValue = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        const r = parseInt(convertedValue[1], 16),
              g = parseInt(convertedValue[2], 16),
              b = parseInt(convertedValue[3], 16);
        return {r, g, b}
    }

    hexToGlsl(hex) {
        const {r, g, b } = this.hexToRgb(hex)
        return new Vec3(r/255, g/255, b/255)
    }
}
